import React, { useEffect, useRef, useState } from 'react'
import { X, ExternalLink, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import VideoPlaybackFallback from './VideoPlaybackFallback'

export const VideoPlayerSimple = ({ video, onClose }) => {
  const modalRef = useRef(null)
  const videoRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [videoUrl, setVideoUrl] = useState(null)
  const [playbackError, setPlaybackError] = useState(false)
  const [showFallback, setShowFallback] = useState(false)
  const [convertedVideoUrl, setConvertedVideoUrl] = useState(null)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    const loadVideo = async () => {
      try {
        setLoading(true)
        setError(null)

        console.log('Loading complete video:', video.title)

        // For simplified complete videos, get direct R2 download URL
        const token = localStorage.getItem('token')
        const response = await fetch(`/.netlify/functions/r2-download-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            objectKey: `videos/${video.cloudflare_uid || video.cloudflareUid}/${video.filename}`
          })
        })

        if (!response.ok) {
          throw new Error('Failed to get video URL')
        }

        const data = await response.json()
        console.log('Got video URL:', data.downloadUrl)

        setVideoUrl(data.downloadUrl)
        setLoading(false)

      } catch (error) {
        console.error('Error loading video:', error)
        setError(error.message)
        setLoading(false)
        toast.error('Failed to load video')
      }
    }

    if (video) {
      loadVideo()
    }

    return () => {
      // Cleanup
      if (videoUrl) {
        console.log('Cleaning up video URL')
      }
    }
  }, [video])

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  // Handle video playback error with smart fallback
  const handleVideoError = (e) => {
    console.error('Video playback error:', e)
    const video = e.target
    console.error('Video error details:', {
      error: video.error,
      networkState: video.networkState,
      readyState: video.readyState,
      currentSrc: video.currentSrc,
      errorCode: video.error?.code,
      errorMessage: video.error?.message
    })

    // Always show fallback for video playback errors (be more aggressive about catching codec issues)
    console.log('🎥 Video playback failed - showing fallback options for codec analysis')
    setPlaybackError(true)
    setShowFallback(true)

    // Also set a timeout to ensure fallback stays visible
    setTimeout(() => {
      if (showFallback) {
        console.log('🎥 Fallback modal should be visible now')
      }
    }, 100)
  }

  const handleConvertedVideo = (convertedUrl, convertedFile) => {
    console.log('✅ Using converted video for playback')
    setConvertedVideoUrl(convertedUrl)
    setShowFallback(false)
    setPlaybackError(false)
    setError(null)
  }

  const handleRetryPlayback = () => {
    console.log('🔄 Retrying video playback')
    setPlaybackError(false)
    setShowFallback(false)
    setError(null)
    setRetryCount(prev => prev + 1)

    // Force video reload
    if (videoRef.current) {
      videoRef.current.load()
    }
  }

  // Detect if video loads but fails to play (no video/audio tracks)
  const handleLoadedMetadata = (e) => {
    const video = e.target
    console.log('Video metadata loaded:', {
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
      duration: video.duration,
      hasVideo: video.videoWidth > 0,
      hasAudio: video.mozHasAudio || Boolean(video.webkitAudioDecodedByteCount) || Boolean(video.audioTracks && video.audioTracks.length)
    })

    // If video loaded but has no visual track, this might be a codec issue
    if (video.duration > 0 && video.videoWidth === 0 && video.videoHeight === 0) {
      console.log('⚠️ Video loaded but has no visual track - possible codec issue')
      setTimeout(() => {
        if (!video.videoWidth && !video.videoHeight) {
          console.log('🎥 Confirmed: No video track playback - showing fallback')
          setPlaybackError(true)
          setShowFallback(true)
        }
      }, 2000) // Give it 2 seconds to start playing
    }
  }

  if (!video) return null

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-white truncate">{video.title}</h2>
            {video.description && (
              <p className="text-gray-400 text-sm mt-1 line-clamp-2">{video.description}</p>
            )}
          </div>
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => window.open(videoUrl, '_blank')}
              disabled={!videoUrl}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              title="Open in new tab"
            >
              <ExternalLink className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Video Content */}
        <div className="relative bg-black" style={{ aspectRatio: '16/9' }}>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
                <p className="text-gray-300">Loading video...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-red-400">
                <AlertCircle className="w-12 h-12 mx-auto mb-4" />
                <p className="text-lg font-medium">Error loading video</p>
                <p className="text-sm text-gray-400 mt-2">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {(videoUrl || convertedVideoUrl) && !loading && !error && !showFallback && (
            <video
              ref={videoRef}
              key={`${videoUrl}-${convertedVideoUrl}-${retryCount}`} // Force re-render on retry
              className="w-full h-full object-contain"
              controls
              autoPlay
              preload="metadata"
              onLoadStart={() => console.log('Video load started')}
              onCanPlay={(e) => {
                console.log('Video can play')
                const video = e.target
                console.log('Video dimensions on canPlay:', {
                  videoWidth: video.videoWidth,
                  videoHeight: video.videoHeight,
                  duration: video.duration,
                  hasVisualTrack: video.videoWidth > 0 && video.videoHeight > 0,
                  currentSrc: video.currentSrc
                })
              }}
              onLoadedMetadata={handleLoadedMetadata}
              onError={handleVideoError}
              style={{ minHeight: '300px', backgroundColor: '#000' }}
            >
              <source
                src={convertedVideoUrl || videoUrl}
                type={convertedVideoUrl ? 'video/mp4' : (video.mime_type || 'video/mp4')}
              />
              Your browser does not support the video tag.
            </video>
          )}

          {/* Playback Fallback for Codec Issues */}
          {showFallback && videoUrl && !loading && (
            <VideoPlaybackFallback
              videoUrl={videoUrl}
              video={video}
              onConvertedVideo={handleConvertedVideo}
              onRetry={handleRetryPlayback}
            />
          )}
        </div>

        {/* Video Info */}
        <div className="p-4 border-t border-gray-700 bg-gray-800">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-400">
            <div>
              <span className="font-medium">Category:</span>
              <span className="ml-2 capitalize">{video.category || 'Uncategorized'}</span>
            </div>
            <div>
              <span className="font-medium">Size:</span>
              <span className="ml-2">{video.file_size ? `${(video.file_size / 1024 / 1024).toFixed(1)} MB` : 'Unknown'}</span>
            </div>
            <div>
              <span className="font-medium">Format:</span>
              <span className="ml-2">{video.mime_type || 'video/mp4'}</span>
            </div>
            <div>
              <span className="font-medium">Upload:</span>
              <span className="ml-2">{new Date(video.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VideoPlayerSimple